import { useQuery } from "@tanstack/react-query";
import { useEffect, useRef } from "react";
import { fetchHelpScreenArticles } from "@/api/help";
import { HelpArticleCard } from "@/components/help/HelpArticleCard";
import { useDelayedFlag } from "@/components/help/useDelayedFlag";
import { GfgButton } from "@/components/ui/GfgButton";
import { HELP_RAIL_FALLBACK_LABEL, HELP_TOPICS } from "@/constants/help";
import type { HelpSection } from "@/interfaces/help";
import type { HelpScreenKey } from "@/interfaces/helpScreens";
import { apiErrorMessage } from "@/utils/apiError-helper";

const SKELETON_KEYS = ["skeleton-1", "skeleton-2", "skeleton-3"];

export interface HelpScreenViewProps {
  contextKeys: readonly HelpScreenKey[];
  screenLabel: string | null;
  /** Focus this view's heading on show — set when it was reached from inside the panel. */
  focusOnShow: boolean;
  onSelectArticle: (slug: string) => void;
  onSelectSection: (section: HelpSection) => void;
}

/**
 * The panel's home view: the current screen's article cut, rendered as prototype rows
 * ("rules, not cards" — content/design/client-experience-prototype.html's `.hc-list`). Below
 * 900px, where the rail is hidden, it ends with the prototype's `.hc-topics` chips instead.
 */
export function HelpScreenView({
  contextKeys,
  screenLabel,
  focusOnShow,
  onSelectArticle,
  onSelectSection,
}: HelpScreenViewProps) {
  const headingRef = useRef<HTMLHeadingElement>(null);
  const {
    data: articles,
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["help", "screen", ...contextKeys],
    queryFn: () => fetchHelpScreenArticles(contextKeys),
  });

  const showSkeleton = useDelayedFlag(isLoading);
  const isFirstRun = !isLoading && !isError && !!articles?.length;

  useEffect(() => {
    if (focusOnShow) headingRef.current?.focus();
  }, [focusOnShow]);

  return (
    <div className="mx-auto w-full max-w-[560px] max-[900px]:max-w-none">
      <h2
        ref={headingRef}
        tabIndex={-1}
        className="m-0 font-crimson font-normal text-[24px] text-rodeo-black outline-none"
      >
        {screenLabel ?? HELP_RAIL_FALLBACK_LABEL}
      </h2>
      <p className="mt-[5px] max-w-[60ch] text-[13px] text-graphite">
        Answers picked by our team for the screen you're on.
      </p>

      <div className="mt-5 flex flex-col gap-3">
        {showSkeleton &&
          SKELETON_KEYS.map((key) => (
            <div key={key} className="h-16 animate-pulse rounded-card bg-wax" aria-hidden="true" />
          ))}

        {isError && (
          <div className="flex flex-col items-center gap-3 rounded-card border border-card-border py-10 text-center">
            <p className="text-[14px] text-graphite">
              {apiErrorMessage(error, "We couldn't load help articles. Please try again.")}
            </p>
            <GfgButton type="button" variant="outline" onClick={() => refetch()}>
              Retry
            </GfgButton>
          </div>
        )}

        {!isLoading && !isError && articles?.length === 0 && (
          <div className="rounded-card border border-card-border py-10 text-center">
            <p className="text-[14px] text-graphite">No help articles for this screen yet.</p>
          </div>
        )}

        {isFirstRun && (
          <p className="text-[13px] text-graphite">Select an article below to open it here.</p>
        )}

        {!isLoading && !isError && !!articles?.length && (
          <div className="flex flex-col">
            {articles.map((article) => (
              <HelpArticleCard key={article.slug} article={article} onSelect={onSelectArticle} />
            ))}
          </div>
        )}
      </div>

      <div
        className={`hidden max-[900px]:block ${
          isFirstRun ? "mt-[26px]" : "mt-[30px] border-smoke border-t pt-5"
        }`}
      >
        <p className="m-0 mb-3 font-crimson font-normal text-[20px] text-rodeo-black">
          Or browse by topic
        </p>
        <div className="flex flex-wrap gap-[7px]">
          {HELP_TOPICS.map((t) => (
            <button
              key={t.section}
              type="button"
              onClick={() => onSelectSection(t.section)}
              className="cursor-pointer rounded-[20px] border border-smoke bg-white px-3 py-1.5 font-lato font-semibold text-[12px] text-rodeo-black hover:border-dark-dust"
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

export default HelpScreenView;
